import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Archive, Briefcase, Pencil, PenLine } from "lucide-react";
import { entitiesApi } from "@/api/counterparties";
import { ApiError } from "@/api/client";
import type { EntityDetailDto } from "@/types/counterparties";
import { COUNTERPARTY_PERMISSIONS } from "@/types/counterparties";
import {
  Badge,
  Button,
  DetailField as Field,
  DetailFieldList,
  RECORD_CARD_MIN_WIDTH,
  RECORD_RAIL_WIDTH,
  ResponsiveGrid,
  ResponsiveSplit,
} from "@/design-system/components/ui";
import { PageHeader } from "@/components/layout/PageHeader";
import { useBreadcrumbs } from "@/components/layout/BreadcrumbsContext";
import { useAuth } from "@/hooks/useAuth";
import { useDialogs } from "@/hooks/useDialogs";
import { useToast } from "@/components/ui/Toast";
import { CounterpartyStatusBadge } from "@/components/counterparties/StatusBadge";
import { RequestDocumentsButton } from "@/components/Collection/RequestDocumentsButton";
import { CounterpartyDealsTab } from "@/components/counterparties/CounterpartyDealsTab";
import { ActiveDealsBlockedDialog } from "@/components/counterparties/ActiveDealsBlockedDialog";
import {
  AuthorizedSignersList,
  parseSignersJson,
  serializeSigners,
} from "@/components/counterparties/AuthorizedSignersList";
import type { AuthorizedSigner } from "@/types/counterparties";
import { DocumentPanel } from "@/pages/documents/components/DocumentPanel";
import { TaskListMini } from "@/pages/tasks/components/TaskListMini";
import { AppointmentListMini } from "@/pages/calendar/components/AppointmentListMini";
import { CommunicationsTabPanel } from "@/pages/communications/components/CommunicationsTabPanel";
import { NotesTabPanel } from "@/pages/notes/components/NotesTabPanel";
import { ActivityTabPanel } from "@/pages/activity/components/ActivityTabPanel";
import { RelatedConversationsPanel } from "@/components/messaging/RelatedConversationsPanel";
import { OverviewActivityPreview } from "@/components/activity/OverviewActivityPreview";
import { useOpenTaskCountByRelated } from "@/pages/tasks/hooks/useWorkTasks";
import { useCommunicationsRecentCount } from "@/pages/communications/hooks/useCommunications";
import { useNotesRecentCount } from "@/pages/notes/hooks/useNotes";
import { useActivityRecentCount } from "@/pages/activity/hooks/useActivityByOwner";
import {
  CROSS_CUTTING_TAB_ICONS,
  EntityTabBar,
  type EntityTabItem,
} from "@/components/layout/EntityTabBar";
import { useDateFormatter } from "@/utils/datetime";
import { displayEmail, NO_EMAIL_ON_FILE } from "@/design-system/lib/format";
import { entityKeys, invalidateCounterpartyReads } from "../counterpartyKeys";

const PERMS = COUNTERPARTY_PERMISSIONS.Entity;

// Layout Compliance v1.0 PR 9 — Entity migration. The "Authorized
// signers" tab is entity-specific and keeps its position before the
// cross-cutting block per Layout Standard §3.
type TabId =
  | "overview"
  | "signers"
  | "deals"
  | "documents"
  | "tasks"
  | "calendar"
  | "messages"
  | "communications"
  | "notes"
  | "activity";

const TABS: readonly TabId[] = [
  "overview",
  "signers",
  "deals",
  "documents",
  "tasks",
  "calendar",
  "messages",
  "communications",
  "notes",
  "activity",
] as const;

const TAB_ITEMS: ReadonlyArray<EntityTabItem<TabId>> = [
  { key: "overview", label: "Overview", icon: CROSS_CUTTING_TAB_ICONS.overview },
  { key: "signers", label: "Authorized signers", icon: PenLine },
  { key: "deals", label: "Deals", icon: Briefcase },
  { key: "documents", label: "Documents", icon: CROSS_CUTTING_TAB_ICONS.documents },
  { key: "tasks", label: "Tasks", icon: CROSS_CUTTING_TAB_ICONS.tasks },
  { key: "calendar", label: "Calendar", icon: CROSS_CUTTING_TAB_ICONS.calendar },
  { key: "messages", label: "Messages", icon: CROSS_CUTTING_TAB_ICONS.messages },
  { key: "communications", label: "Communications", icon: CROSS_CUTTING_TAB_ICONS.communications },
  { key: "notes", label: "Notes", icon: CROSS_CUTTING_TAB_ICONS.notes },
  { key: "activity", label: "Activity", icon: CROSS_CUTTING_TAB_ICONS.activity },
] as const;

function isTabId(value: string | null): value is TabId {
  return value !== null && (TABS as readonly string[]).includes(value);
}

export function EntityDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const dialogs = useDialogs();
  const toast = useToast();
  const { hasPermission } = useAuth();
  const { date } = useDateFormatter();
  const [archiveBlocked, setArchiveBlocked] = useState(false);

  const [params, setParams] = useSearchParams();
  const tabParam = params.get("tab");
  const tab: TabId = isTabId(tabParam) ? tabParam : "overview";

  const setTab = useCallback(
    (next: TabId) => {
      const updated = new URLSearchParams(params);
      if (next === "overview") updated.delete("tab");
      else updated.set("tab", next);
      setParams(updated, { replace: true });
    },
    [params, setParams],
  );

  useEffect(() => {
    function isTypingTarget(t: EventTarget | null): boolean {
      if (!(t instanceof HTMLElement)) return false;
      if (t.isContentEditable) return true;
      const tag = t.tagName;
      return tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT";
    }
    function handler(e: KeyboardEvent) {
      if (isTypingTarget(e.target)) return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      const idx = Number(e.key);
      if (Number.isInteger(idx) && idx >= 1 && idx <= TABS.length) {
        e.preventDefault();
        setTab(TABS[idx - 1]);
      }
    }
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [setTab]);

  const canUpdate = hasPermission(PERMS.Update);
  const canDelete = hasPermission(PERMS.Delete);

  const entityQuery = useQuery({
    queryKey: entityKeys.detail(id),
    queryFn: () => entitiesApi.get(id!),
    enabled: !!id,
  });

  useBreadcrumbs(
    entityQuery.data
      ? [
          { label: "Entities", href: "/counterparties/entities" },
          { label: entityQuery.data.legalEntityName },
        ]
      : null,
  );

  const archive = useMutation({
    mutationFn: () => entitiesApi.archive(id!),
    onSuccess: () => {
      invalidateCounterpartyReads(queryClient, "entity");
      toast.success("Entity archived.");
      navigate("/counterparties/entities");
    },
    onError: (e) => {
      if (e instanceof ApiError && e.status === 409) {
        setArchiveBlocked(true);
      } else {
        toast.error(e instanceof Error ? e.message : "Archive failed.");
      }
    },
  });

  const updateSigners = useMutation({
    mutationFn: async (next: AuthorizedSigner[]) => {
      const e = entityQuery.data!;
      return entitiesApi.update(e.id, {
        status: e.status,
        legalEntityName: e.legalEntityName,
        dba: e.dba ?? null,
        entityType: e.entityType,
        stateOfFormation: e.stateOfFormation ?? null,
        dateOfFormation: e.dateOfFormation ?? null,
        ein: null,
        formationStatus: e.formationStatus ?? null,
        primaryEmail: e.primaryEmail,
        primaryPhone: e.primaryPhone,
        address: e.address,
        mailingAddress: e.mailingAddress ?? null,
        accreditedInvestorStatus: e.accreditedInvestorStatus ?? null,
        accreditedInvestorBasis: e.accreditedInvestorBasis ?? null,
        taxFilingStatus: e.taxFilingStatus ?? null,
        originalSaleDate: e.originalSaleDate ?? null,
        originalSaleBasisInfo: e.originalSaleBasisInfo ?? null,
        bankACHName: e.bankACHName ?? null,
        bankRoutingNumber: null,
        bankAccountNumber: null,
        bankAccountType: e.bankAccountType ?? null,
        authorizedSignersJson: serializeSigners(next),
        tags: e.tags,
      });
    },
    onSuccess: () => {
      invalidateCounterpartyReads(queryClient, "entity");
      toast.success("Authorized signers updated.");
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : "Update failed."),
  });

  const signers = useMemo(
    () => parseSignersJson(entityQuery.data?.authorizedSignersJson),
    [entityQuery.data?.authorizedSignersJson]
  );

  const openTaskCountQuery = useOpenTaskCountByRelated("Entity", id ?? undefined);
  const openTaskCount = openTaskCountQuery.data ?? 0;
  const commsRecent = useCommunicationsRecentCount("entity", id ?? "", 30);
  const notesRecent = useNotesRecentCount("entity", id ?? "", 7);
  const activityRecent = useActivityRecentCount("entity", id ?? "", 7);

  if (entityQuery.isLoading) {
    return <div className="text-caption uppercase tracking-wide text-gray-mid">Loading…</div>;
  }
  if (entityQuery.isError || !entityQuery.data) {
    return (
      <div className="text-small text-status-error">
        {(entityQuery.error as Error)?.message ?? "Entity not found."}
      </div>
    );
  }

  const entity = entityQuery.data;

  const onArchive = async () => {
    const ok = await dialogs.confirm({
      title: `Archive ${entity.legalEntityName}?`,
      message: "This entity will no longer appear in active lists. Existing deal references are preserved.",
      confirmLabel: "Archive",
      destructive: true,
    });
    if (!ok) return;
    archive.mutate();
  };

  const tabBadge = (key: TabId): string | number | null => {
    switch (key) {
      case "signers":
        return signers.length > 0 ? signers.length : null;
      case "tasks":
        return openTaskCount > 0 ? openTaskCount : null;
      case "communications":
        return commsRecent.data && commsRecent.data > 0 ? commsRecent.data : null;
      case "notes":
        return notesRecent.data && notesRecent.data > 0 ? notesRecent.data : null;
      case "activity":
        return activityRecent.data && activityRecent.data > 0 ? activityRecent.data : null;
      default:
        return null;
    }
  };

  return (
    <>
      <PageHeader
        eyebrow="Counterparties · Entity"
        title={entity.legalEntityName}
        actions={
          <>
            <RequestDocumentsButton
              ownerEntityType="Entity"
              ownerEntityId={entity.id}
              ownerDisplayName={entity.legalEntityName}
              size="sm"
            />
            {canUpdate && (
              <Button size="sm" variant="secondary" onClick={() => navigate(`/counterparties/entities/${entity.id}/edit`)}>
                <Pencil size={14} aria-hidden /> Edit
              </Button>
            )}
            {canDelete && entity.status === "Active" && (
              <Button size="sm" variant="destructive" onClick={onArchive} disabled={archive.isPending}>
                <Archive size={14} aria-hidden /> Archive
              </Button>
            )}
          </>
        }
      />

      <div className="flex items-center gap-space-3 -mt-space-3 mb-space-4">
        <CounterpartyStatusBadge status={entity.status} />
        <Badge status="info">{entity.entityType}</Badge>
        <span className="text-caption text-gray-mid">
          {entity.stateOfFormation ?? "—"} · Created {date(entity.createdAt)}
        </span>
      </div>

      <EntityTabBar
        ariaLabel="Entity sections"
        testIdPrefix="entity"
        items={TAB_ITEMS.map((it) => ({ ...it, badge: tabBadge(it.key) }))}
        value={tab}
        onChange={setTab}
      />

      <div className="pt-space-4">
        {tab === "overview" && (
          // Container-driven — Table & Tile Standard §12. A `lg:` breakpoint
          // asks the WINDOW how wide this box is, which is the one thing the
          // window cannot answer once the box is a tab body or a drawer.
          <ResponsiveSplit
            gap="md"
            mainMinWidth="34rem"
            railWidth={RECORD_RAIL_WIDTH}
            railGrows={false}
            main={<EntityOverview entity={entity} />}
            rail={
              <OverviewActivityPreview
                ownerType="entity"
                ownerId={entity.id}
                detailPath={`/counterparties/entities/${entity.id}`}
              />
            }
          />
        )}
        {tab === "signers" && (
          <AuthorizedSignersList
            signers={signers}
            canEdit={canUpdate}
            onChange={(next) => updateSigners.mutate(next)}
          />
        )}
        {tab === "deals" && <CounterpartyDealsTab kind="Entity" entityId={entity.id} />}
        {tab === "documents" && (
          <DocumentPanel ownerType="Entity" ownerId={entity.id} rootName={entity.legalEntityName} />
        )}
        {tab === "tasks" && (
          <TaskListMini
            relatedEntityType="Entity"
            relatedEntityId={entity.id}
            addButtonLabel="+ Add task linked to this entity"
          />
        )}
        {tab === "calendar" && (
          <AppointmentListMini
            relatedEntityType="Entity"
            relatedEntityId={entity.id}
            entityLabel={entity.legalEntityName}
          />
        )}
        {tab === "messages" && (
          <RelatedConversationsPanel
            resourceType="entity"
            resourceId={entity.id}
            entityLabel={entity.legalEntityName}
          />
        )}
        {tab === "communications" && (
          <CommunicationsTabPanel
            ownerType="entity"
            ownerId={entity.id}
            ownerLabel={entity.legalEntityName}
          />
        )}
        {tab === "notes" && <NotesTabPanel ownerType="entity" ownerId={entity.id} />}
        {tab === "activity" && <ActivityTabPanel ownerType="entity" ownerId={entity.id} />}
      </div>

      {archiveBlocked && (
        <ActiveDealsBlockedDialog
          open
          displayName={entity.legalEntityName}
          onClose={() => setArchiveBlocked(false)}
        />
      )}
    </>
  );
}

function EntityOverview({ entity }: { entity: EntityDetailDto }) {
  return (
    <ResponsiveGrid cols={2} minColWidth={RECORD_CARD_MIN_WIDTH} gap="md">
      <Card title="Identity">
        <DetailFieldList>
          <Field label="Legal name">{entity.legalEntityName}</Field>
          <Field label="DBA">{entity.dba ?? "—"}</Field>
          <Field label="Type">{entity.entityType}</Field>
          <Field label="State of formation">{entity.stateOfFormation ?? "—"}</Field>
          <Field label="Date of formation">{entity.dateOfFormation ?? "—"}</Field>
          <Field label="EIN"><span className="font-mono">{entity.einMasked ?? "—"}</span></Field>
          <Field label="Formation status">{entity.formationStatus ?? "—"}</Field>
        </DetailFieldList>
      </Card>

      <Card title="Contact">
        <DetailFieldList>
          <Field label="Primary email">
            {displayEmail(entity.primaryEmail) ?? (
              <span className="text-gray-mid">{NO_EMAIL_ON_FILE}</span>
            )}
          </Field>
          <Field label="Primary phone">{entity.primaryPhone}</Field>
        </DetailFieldList>
      </Card>

      <Card title="Address">
        <AddressBlock label="Physical" address={entity.address} />
        {entity.mailingAddress && (
          <div className="mt-space-3">
            <AddressBlock label="Mailing" address={entity.mailingAddress} />
          </div>
        )}
      </Card>

      <Card title="Investor / tax">
        <DetailFieldList>
          <Field label="Accredited investor">
            {entity.accreditedInvestorStatus ?? "—"}
            {entity.accreditedInvestorBasis ? ` (${entity.accreditedInvestorBasis})` : ""}
          </Field>
          <Field label="Tax filing status">{entity.taxFilingStatus ?? "—"}</Field>
          <Field label="Original sale date">{entity.originalSaleDate ?? "—"}</Field>
          <Field label="Original sale basis">{entity.originalSaleBasisInfo ?? "—"}</Field>
        </DetailFieldList>
      </Card>

      <Card title="Banking">
        <DetailFieldList>
          <Field label="ACH name">{entity.bankACHName ?? "—"}</Field>
          <Field label="Routing #"><span className="font-mono">{entity.bankRoutingNumberMasked ?? "—"}</span></Field>
          <Field label="Account #"><span className="font-mono">{entity.bankAccountNumberMasked ?? "—"}</span></Field>
          <Field label="Account type">{entity.bankAccountType ?? "—"}</Field>
        </DetailFieldList>
      </Card>

      <Card title="Tags">
        <div className="flex flex-wrap gap-space-1">
          {entity.tags.length === 0 ? (
            <span className="text-caption text-gray-mid">No tags</span>
          ) : (
            entity.tags.map((t) => <Badge key={t} status="gold">{t}</Badge>)
          )}
        </div>
      </Card>
    </ResponsiveGrid>
  );
}

function Card({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="bg-white-pure border border-border-light rounded-lg p-space-4">
      <h3 className="font-display font-bold text-small uppercase tracking-wider text-gold-shadow mb-space-3 m-0">
        {title}
      </h3>
      <div className="grid grid-cols-1 gap-space-2">{children}</div>
    </section>
  );
}

function AddressBlock({ label, address }: { label: string; address: { streetAddress1: string; streetAddress2?: string | null; city: string; state: string; zip: string; country: string } }) {
  return (
    <div className="text-small">
      <div className="text-caption uppercase tracking-wide text-gray-mid mb-space-1">{label}</div>
      <div className="text-onyx">{address.streetAddress1}</div>
      {address.streetAddress2 && <div className="text-onyx">{address.streetAddress2}</div>}
      <div className="text-onyx">{address.city}, {address.state} {address.zip}</div>
      <div className="text-gray-mid">{address.country}</div>
    </div>
  );
}
